import { useEffect, useRef } from "react";
import type { CompaniaArbol, SucursalArbol } from "../../types/accesosOrganizacion.types";
import { estadoGrupo, sedesDeCompania, sedesDeSucursal } from "../../utils/arbolOrganizacion";
import { IconoChevron, IconoEdificio, IconoSucursal } from "./iconos";

interface ArbolOrganizacionProps {
    arbol: CompaniaArbol[];
    borrador: Set<number>;
    marcar: (codigos: number[], valor: boolean) => void;
    colapsados: Set<string>;
    alternar: (clave: string) => void;
    companiaUsuario: string;
}

// Casilla con estado intermedio (algunas sedes marcadas).
const CasillaGrupo = ({ codigos, borrador, marcar, etiqueta }: any) => {
    const ref = useRef<HTMLInputElement>(null)
    const estado = estadoGrupo(codigos, borrador)
    useEffect(() => { if (ref.current) ref.current.indeterminate = estado === "algunos" }, [estado])
    return (
        <input ref={ref} type="checkbox" aria-label={`Marcar ${etiqueta}`} className="form-check-input tw-m-0 tw-cursor-pointer"
            checked={estado === "todos"} onChange={() => marcar(codigos, estado !== "todos")} />
    )
}

// Contador "marcadas/total" con color según el estado.
const Contador = ({ codigos, borrador }: { codigos: number[]; borrador: Set<number> }) => {
    const marcadas = codigos.filter((c) => borrador.has(c)).length
    const tono = marcadas === 0 ? "tw-bg-gray-100 tw-text-gray-500" : marcadas === codigos.length ? "tw-bg-primario tw-text-white" : "tw-bg-indigo-100 tw-text-indigo-700"
    return <span className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-whitespace-nowrap ${tono}`}>{marcadas}/{codigos.length}</span>
}

// Cabecera de compañía o sucursal: flecha, casilla, ícono, nombre, contador y Todas/Ninguna.
const FilaGrupo = ({ clave, tipo, icono, nombre, insignia, codigos, borrador, marcar, colapsados, alternar }: any) => {
    const abierto = !colapsados.has(clave)
    return (
        <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2">
            <button type="button" className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded tw-border-0 tw-bg-transparent tw-text-gray-500 hover:tw-bg-gray-200"
                aria-label={abierto ? `Contraer ${nombre}` : `Expandir ${nombre}`} aria-expanded={abierto} onClick={() => alternar(clave)}>
                <IconoChevron abierto={abierto} />
            </button>
            <CasillaGrupo codigos={codigos} borrador={borrador} marcar={marcar} etiqueta={`${tipo} ${nombre}`} />
            <span className="tw-text-gray-400">{icono}</span>
            <div className="tw-min-w-0 tw-flex-1 tw-leading-tight">
                <div className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-400">{tipo}</div>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-gray-800">
                    <span className="tw-truncate">{nombre}</span>{insignia}
                </div>
            </div>
            <Contador codigos={codigos} borrador={borrador} />
            <div className="tw-hidden sm:tw-flex tw-items-center tw-gap-1 tw-text-xs">
                <button type="button" className="tw-rounded tw-border-0 tw-bg-transparent tw-px-1.5 tw-py-0.5 tw-text-primario hover:tw-bg-indigo-50" onClick={() => marcar(codigos, true)}>Todas</button>
                <button type="button" className="tw-rounded tw-border-0 tw-bg-transparent tw-px-1.5 tw-py-0.5 tw-text-gray-500 hover:tw-bg-gray-100" onClick={() => marcar(codigos, false)}>Ninguna</button>
            </div>
        </div>
    )
}

// Sedes como chips seleccionables.
const SedesDeSucursal = ({ sucursal, borrador, marcar }: { sucursal: SucursalArbol } & any) => (
    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-2 tw-pl-14 tw-pr-3 tw-pb-3">
        {sucursal.sedes.map((sede) => {
            const activa = borrador.has(sede.codigo)
            return (
                <label key={sede.codigo} className={`tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-px-2.5 tw-py-1.5 tw-text-sm tw-cursor-pointer tw-transition-colors ${activa ? "tw-border-primario tw-bg-indigo-50 tw-text-gray-900 tw-font-medium" : "tw-border-gray-200 tw-bg-white tw-text-gray-700 hover:tw-border-gray-300 hover:tw-bg-gray-50"}`}>
                    <input type="checkbox" className="form-check-input tw-m-0" checked={activa} onChange={(e) => marcar([sede.codigo], e.target.checked)} />
                    <span className="tw-truncate">{sede.nombre}</span>
                    {sede.estado !== "A" && <span className="tw-ml-auto tw-rounded tw-bg-gray-100 tw-px-1.5 tw-text-[10px] tw-text-gray-500">Inactiva</span>}
                </label>
            )
        })}
    </div>
)

const InsigniaUsuario = () => (
    <span className="tw-rounded-full tw-bg-emerald-50 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-text-emerald-700 tw-border tw-border-emerald-200">Compañía del usuario</span>
)

// Árbol Compañía > Sucursal > Sede con casillas.
export const ArbolOrganizacion = ({ arbol, borrador, marcar, colapsados, alternar, companiaUsuario }: ArbolOrganizacionProps) => {
    if (arbol.length === 0) {
        return <p className="tw-p-6 tw-text-center tw-text-sm tw-text-gray-500">No hay sedes que coincidan con la búsqueda.</p>
    }
    return (
        <div className="tw-flex tw-flex-col tw-gap-3 tw-p-3">
            {arbol.map((compania) => (
                <div key={compania.codigo} className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white">
                    <div className="tw-bg-gray-50">
                        <FilaGrupo clave={`c:${compania.codigo}`} tipo="Compañía" icono={<IconoEdificio />} nombre={compania.nombre}
                            insignia={compania.codigo === companiaUsuario ? <InsigniaUsuario /> : null}
                            codigos={sedesDeCompania(compania)} borrador={borrador} marcar={marcar} colapsados={colapsados} alternar={alternar} />
                    </div>
                    {!colapsados.has(`c:${compania.codigo}`) && compania.sucursales.map((sucursal) => (
                        <div key={sucursal.codigo} className="tw-border-t tw-border-gray-100 tw-pl-4">
                            <FilaGrupo clave={`s:${sucursal.codigo}`} tipo="Sucursal / empresa" icono={<IconoSucursal />} nombre={sucursal.nombre}
                                codigos={sedesDeSucursal(sucursal)} borrador={borrador} marcar={marcar} colapsados={colapsados} alternar={alternar} />
                            {!colapsados.has(`s:${sucursal.codigo}`) &&
                                <SedesDeSucursal sucursal={sucursal} borrador={borrador} marcar={marcar} />}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    )
}
